import { useCallback, useEffect, useMemo, useState } from 'react';
import { timeApi, type TimeEntry } from '../../api';
import Select from '../common/Select';

type Props = { matterId: number | string };

const ACTIVITY_CODES: Array<{ value: string; label: string }> = [
  { value: '',              label: '—' },
  { value: 'consultation',  label: 'Consultation' },
  { value: 'research',      label: 'Research' },
  { value: 'drafting',      label: 'Drafting' },
  { value: 'appearance',    label: 'Appearance' },
  { value: 'travel',        label: 'Travel' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'perusal',       label: 'Perusal' },
];

const asBool = (v: unknown) => v === true || v === 'true';

// "HH:MM" → minutes since midnight. Returns NaN on bad input.
function hhmmToMinutes(s: string): number {
  if (!/^\d{2}:\d{2}$/.test(s)) return NaN;
  const [h, m] = s.split(':').map(Number);
  return h * 60 + m;
}
function hoursBetween(start: string, end: string): number {
  const a = hhmmToMinutes(start), b = hhmmToMinutes(end);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b <= a) return 0;
  return +((b - a) / 60).toFixed(2);
}

export default function TimePanel({ matterId }: Props) {
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await timeApi.list({ matter_id: matterId });
      setEntries(r.time_entries || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    let hours = 0, billableHours = 0, unbilledHours = 0;
    for (const e of entries) {
      const h = Number(e.duration_hours) || 0;
      hours += h;
      if (asBool(e.billable)) billableHours += h;
      if (e.billing_status === 'unbilled') unbilledHours += h;
    }
    return { hours, billableHours, unbilledHours };
  }, [entries]);

  const remove = async (id: number | string) => {
    if (!confirm('Delete this time entry?')) return;
    await timeApi.remove(id);
    load();
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Time entries</h3>
        <div className="muted" style={{ fontSize: 12 }}>
          {totals.hours.toFixed(2)}h total · {totals.billableHours.toFixed(2)}h billable · {totals.unbilledHours.toFixed(2)}h unbilled
        </div>
        <button className="primary" onClick={() => setAdding(true)}>+ Log time</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : entries.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No time logged yet. Use "Log time" to record attendance, drafting, research, or appearances.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ width: 120 }}>Time</th>
                <th style={{ width: 110 }}>Activity</th>
                <th>Description</th>
                <th style={{ width: 80, textAlign: 'right' }}>Hours</th>
                <th style={{ width: 140 }}>Billing</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={String(e.ROWID)}>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{e.work_date}</td>
                  <td style={{ fontVariantNumeric: 'tabular-nums', fontSize: 13 }}>
                    {e.start_time && e.end_time ? `${e.start_time}–${e.end_time}` : (e.start_time || '—')}
                  </td>
                  <td>{e.activity_code || '—'}</td>
                  <td>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 380 }}>
                      {e.description}
                    </div>
                  </td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {Number(e.duration_hours).toFixed(2)}
                  </td>
                  <td>
                    <span className="pill">{e.billing_status.replace('_', ' ')}</span>
                    {!asBool(e.billable) && (
                      <span className="pill" style={{ marginLeft: 4, background: 'var(--color-info-soft)', color: 'var(--color-info)' }}>
                        non-billable
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    {e.billing_status === 'unbilled' && (
                      <button
                        className="ghost"
                        style={{ padding: '4px 8px', color: 'var(--color-danger)' }}
                        onClick={() => remove(e.ROWID)}
                        title="Delete"
                      >
                        ×
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {adding && (
        <LogTimeModal
          matterId={matterId}
          onClose={() => setAdding(false)}
          onSaved={() => { setAdding(false); load(); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LogTimeModal — supports multiple time slots in one save.
//
// A typical advocate day is fragmented: 08:30–11:45 drafting, 12:30–13:15
// telecon, 14:00–17:00 perusal. Each slot posts as its own `time_entry` so
// the invoice can render an explicit per-slot row. The total hours line is
// derived from the slots, not entered by hand.
// ---------------------------------------------------------------------------

type Slot = { start: string; end: string };

function LogTimeModal({ matterId, onClose, onSaved }: { matterId: number | string; onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [workDate, setWorkDate] = useState(today);
  const [activity, setActivity] = useState('');
  const [description, setDescription] = useState('');
  const [billable, setBillable] = useState(true);
  const [slots, setSlots] = useState<Slot[]>([{ start: '', end: '' }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalHours = useMemo(
    () => slots.reduce((s, sl) => s + hoursBetween(sl.start, sl.end), 0),
    [slots],
  );

  const updateSlot = (i: number, patch: Partial<Slot>) =>
    setSlots((cur) => cur.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const addSlot    = () => setSlots((cur) => [...cur, { start: '', end: '' }]);
  const removeSlot = (i: number) => setSlots((cur) => cur.length > 1 ? cur.filter((_, idx) => idx !== i) : cur);

  const save = async () => {
    const valid = slots.filter((s) => hoursBetween(s.start, s.end) > 0);
    if (!valid.length) { setError('Add at least one valid time slot (start before end).'); return; }
    if (!description.trim()) { setError('A short description is required.'); return; }
    setSaving(true);
    setError(null);
    try {
      // One time_entry per slot — keeps the invoice line-itemised so the
      // PDF prints each "08:31–09:02" segment as its own row.
      for (const s of valid) {
        await timeApi.create({
          matter_id: matterId,
          work_date: workDate,
          start_time: s.start,
          end_time: s.end,
          duration_hours: hoursBetween(s.start, s.end),
          activity_code: activity || undefined,
          description: description.trim(),
          billable,
        });
      }
      onSaved();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>Log time</h3>
        <div className="stack">
          <div className="row">
            <div style={{ flex: 1 }}>
              <label>Date</label>
              <input
                type="date"
                max={today}
                value={workDate}
                onChange={(e) => setWorkDate(e.target.value)}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label>Activity</label>
              <Select
                value={activity}
                onChange={setActivity}
                options={ACTIVITY_CODES}
              />
            </div>
          </div>

          <div>
            <label>Time slots <span className="muted" style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>(add a row for each block of work — e.g. 08:30–11:45 + 15:30–18:00)</span></label>
            <div className="stack" style={{ gap: 6 }}>
              {slots.map((s, i) => {
                const hrs = hoursBetween(s.start, s.end);
                const invalid = (s.start || s.end) && hrs <= 0;
                return (
                  <div key={i} className="row" style={{ alignItems: 'center', gap: 8 }}>
                    <input
                      type="time"
                      value={s.start}
                      onChange={(e) => updateSlot(i, { start: e.target.value })}
                      style={{ width: 130 }}
                    />
                    <span className="muted">–</span>
                    <input
                      type="time"
                      value={s.end}
                      onChange={(e) => updateSlot(i, { end: e.target.value })}
                      style={{ width: 130 }}
                    />
                    <span
                      className={invalid ? '' : 'muted'}
                      style={{
                        fontSize: 12, minWidth: 70, fontVariantNumeric: 'tabular-nums',
                        color: invalid ? 'var(--color-danger)' : undefined,
                      }}
                    >
                      {invalid ? 'end ≤ start' : hrs > 0 ? `${hrs.toFixed(2)} h` : '—'}
                    </span>
                    <button
                      className="ghost"
                      onClick={() => removeSlot(i)}
                      disabled={slots.length === 1}
                      style={{ padding: '4px 10px', fontSize: 12 }}
                      type="button"
                    >
                      Remove
                    </button>
                  </div>
                );
              })}
              <button
                className="ghost"
                onClick={addSlot}
                type="button"
                style={{ alignSelf: 'flex-start', padding: '4px 10px', fontSize: 12 }}
              >
                + Add another slot
              </button>
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Total: <b>{totalHours.toFixed(2)} hours</b>
                {slots.length > 1 && <span> across {slots.filter((s) => hoursBetween(s.start, s.end) > 0).length} slots</span>}
              </div>
            </div>
          </div>

          <div>
            <label>Description</label>
            <textarea
              rows={3}
              placeholder="e.g. Consultation with attorney re settlement proposal…"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <div className="field-help">
              Appears on the invoice. Same description is used for every slot — the times distinguish them.
            </div>
          </div>

          <label className="inline">
            <input
              type="checkbox"
              checked={billable}
              onChange={(e) => setBillable(e.target.checked)}
            />
            <span>Billable</span>
          </label>

          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving} type="button">Cancel</button>
            <button className="primary" onClick={save} disabled={saving} type="button">
              {saving
                ? 'Saving…'
                : slots.filter((s) => hoursBetween(s.start, s.end) > 0).length > 1
                  ? `Log ${slots.filter((s) => hoursBetween(s.start, s.end) > 0).length} entries`
                  : 'Log time'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 620, maxWidth: '92vw' };
